<template>
  <div class="university-detail-page">
    <div v-loading="loading" class="detail-container">
      <!-- 返回按钮 -->
      <div class="header-section">
        <el-button 
          type="text" 
          icon="el-icon-arrow-left" 
          @click="goBack"
          class="back-btn"
        >
          返回
        </el-button>
      </div>

      <!-- 大学基本信息 -->
      <div v-if="universityInfo" class="university-info-section">
        <div class="info-card">
          <div class="info-header">
            <h1 class="university-title">
              {{ universityInfo.name }}
              <span v-if="universityInfo.english_name" class="english-name">
                {{ universityInfo.english_name }}
              </span>
            </h1>
            <div v-if="universityInfo.rank" class="rank-badge">
              <el-tag type="warning" size="medium">排名: {{ universityInfo.rank }}</el-tag>
            </div>
          </div>

          <div class="info-content">
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">
                  <i class="el-icon-location"></i>
                  <span>地区</span>
                </div>
                <div class="info-value">{{ universityInfo.area || '未知' }}</div>
              </div>

              <div class="info-item">
                <div class="info-label">
                  <i class="el-icon-school"></i>
                  <span>类型</span>
                </div>
                <div class="info-value">{{ universityInfo.type || '未知' }}</div>
              </div>

              <div class="info-item">
                <div class="info-label">
                  <i class="el-icon-collection-tag"></i>
                  <span>标签</span>
                </div>
                <div class="info-value">
                  <el-tag 
                    v-for="(tag, index) in parseTags(universityInfo.tag)" 
                    :key="index"
                    size="small"
                    type="info"
                    class="tag-item"
                  >
                    {{ tag }}
                  </el-tag>
                  <span v-if="!universityInfo.tag">未知</span>
                </div>
              </div>

              <div class="info-item">
                <div class="info-label">
                  <i class="el-icon-check"></i>
                  <span>入驻状态</span>
                </div>
                <div class="info-value">
                  <el-tag 
                    :type="universityInfo.is_settled === 1 ? 'success' : 'info'"
                    size="small"
                  >
                    {{ universityInfo.is_settled === 1 ? '已入驻' : '未入驻' }}
                  </el-tag>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 大学排行榜信息 -->
      <div v-if="universityRankList && universityRankList.length > 0" class="rank-list-section">
        <h2 class="section-title">
          <i class="el-icon-trophy"></i>
          专业排行榜
        </h2>
        <div class="rank-table-container">
          <div class="table-wrapper">
            <el-table
              :data="universityRankList"
              stripe
              border
              style="width: 100%"
              :default-sort="{ prop: 'sub_rank', order: 'ascending' }"
            >
            <el-table-column
              prop="subspecialty_name"
              label="专业名称"
              min-width="150"
              show-overflow-tooltip
            >
              <template slot-scope="scope">
                <span 
                  class="major-name-link"
                  @click="handleMajorClick(scope.row)"
                  title="点击查看该专业排行榜"
                >
                  {{ scope.row.subspecialty_name }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              prop="subspecialty_code"
              label="专业代码"
              width="120"
              align="center"
            >
            </el-table-column>
            <el-table-column
              prop="type"
              label="排名类型"
              width="140"
              align="center"
            >
            </el-table-column>
            <el-table-column
              prop="grade"
              label="等级"
              width="100"
              align="center"
            >
              <template slot-scope="scope">
                <el-tag 
                  :type="getGradeTagType(scope.row.grade)"
                  size="small"
                >
                  {{ scope.row.grade }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column
              prop="sub_rank"
              label="排名"
              width="100"
              align="center"
              sortable
            >
              <template slot-scope="scope">
                <span class="rank-number">{{ scope.row.sub_rank }}</span>
              </template>
            </el-table-column>
          </el-table>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else-if="!loading && universityInfo" class="empty-rank-state">
        <i class="el-icon-info"></i>
        <p>暂无专业排行榜信息</p>
      </div>
    </div>
  </div>
</template>

<script>
import { getUniversityInfo } from '@/http/api/category'

export default {
  name: 'UniversityDetail',
  data() {
    return {
      loading: false,
      universityInfo: null,
      universityRankList: []
    }
  },
  created() {
    this.fetchUniversityInfo()
  },
  watch: {
    '$route'(to, from) {
      // 路由变化时重新获取数据
      if (to.query.id !== from.query.id) {
        this.fetchUniversityInfo()
      }
    }
  },
  methods: {
    async fetchUniversityInfo() {
      const universityId = this.$route.query.id
      if (!universityId) {
        this.$message.warning('大学ID不能为空')
        this.goBack()
        return
      }

      this.loading = true
      try {
        const result = await getUniversityInfo({ university_id: parseInt(universityId) })
        
        if (result) {
          this.universityInfo = result.university_info || null
          this.universityRankList = result.university_list_rank || []
          
          if (!this.universityInfo) {
            this.$message.warning('未找到该大学信息')
            this.goBack()
          }
        } else {
          this.$message.error('获取大学信息失败')
          this.goBack()
        }
      } catch (error) {
        console.error('获取大学详情失败:', error)
        this.$message.error(`获取大学详情失败: ${error.message || '未知错误'}`)
        this.goBack()
      } finally {
        this.loading = false
      }
    },

    parseTags(tagString) {
      if (!tagString) return []
      return tagString.split(',').map(tag => tag.trim()).filter(tag => tag)
    },


    getGradeTagType(grade) {
      if (!grade) return 'info'
      if (grade.includes('A+')) return 'danger'
      if (grade.includes('A')) return 'warning'
      if (grade.includes('B+')) return 'success'
      if (grade.includes('B')) return 'info'
      return 'info'
    },

    handleMajorClick(row) {
      console.log('点击专业行数据:', row)
      
      // 获取专业ID，尝试多个可能的字段名
      const subspecialtyId = row.subspecialty_id || row.sub_id || row.id
      const subspecialtyName = row.subspecialty_name || row.sub_name || row.name
      const subspecialtyCode = row.subspecialty_code || row.sub_code || row.code
      
      if (!subspecialtyId && !subspecialtyName) {
        this.$message.warning('专业信息不完整，无法查看排行榜')
        return
      }
      
      // 跳转到专业排行榜页面
      this.$router.push({
        path: '/radar/major/ranking',
        query: {
          id: subspecialtyId,
          name: subspecialtyName,
          code: subspecialtyCode
        }
      })
    },

    goBack() {
      this.$router.go(-1)
    }
  }
}
</script>

<style lang="scss" scoped>
.university-detail-page {
  min-height: 100vh;
  background: #f5f7fa;
  padding: 20px;
}

.detail-container {
  max-width: 1200px;
  margin: 0 auto;
}

.header-section {
  margin-bottom: 20px;
  
  .back-btn {
    font-size: 16px;
    color: #606266;
    padding: 0;
    
    &:hover {
      color: #409eff;
    }
  }
}

.university-info-section {
  margin-bottom: 30px;
}

.info-card {
  background: #ffffff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.info-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f2f5;
}

.university-title {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.english-name {
  font-size: 18px;
  color: #64748b;
  font-style: italic;
  font-weight: normal;
}

.rank-badge {
  margin-left: 12px;
}

.info-content {
  margin-top: 20px;
}

.info-grid {
  display: flex;
  flex-wrap: nowrap;
  gap: 24px;
  align-items: center;
}

.info-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.info-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  flex-shrink: 0;
  white-space: nowrap;
  
  i {
    color: #409eff;
    font-size: 16px;
  }
}

.info-value {
  font-size: 15px;
  color: #1e293b;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.tag-item {
  margin: 0;
}

.rank-list-section {
  margin-top: 30px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  i {
    color: #f39c12;
    font-size: 22px;
  }
}

.rank-table-container {
  background: #ffffff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.table-wrapper {
  max-height: 600px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
}

.rank-number {
  font-weight: 600;
  color: #409eff;
  font-size: 16px;
}

.major-name-link {
  color: #409eff;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s;
  
  &:hover {
    color: #66b1ff;
    text-decoration: underline;
  }
}

.empty-rank-state {
  text-align: center;
  padding: 60px 20px;
  background: #ffffff;
  border-radius: 8px;
  color: #94a3b8;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  
  i {
    font-size: 48px;
    margin-bottom: 16px;
    display: block;
    color: #cbd5e1;
  }
  
  p {
    font-size: 14px;
    margin: 0;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .university-detail-page {
    padding: 10px;
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
  
  .university-title {
    font-size: 22px;
  }
  
  .english-name {
    font-size: 14px;
  }
}
</style>

